import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {NgModule, APP_INITIALIZER} from '@angular/core';
import {LocationStrategy, HashLocationStrategy} from '@angular/common';
import {HTTP_INTERCEPTORS} from '@angular/common/http';
import {FormsModule, ReactiveFormsModule} from "@angular/forms";

import {PerfectScrollbarModule} from 'ngx-perfect-scrollbar';
import {PerfectScrollbarConfigInterface} from 'ngx-perfect-scrollbar';

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
    suppressScrollX: true
};
declare var Stimulsoft: any;

import {AppComponent} from './app.component';
import {ContainerComponent} from './containers/container.component';

const APP_CONTAINERS = [
    ContainerComponent
];

import {
    AppAsideModule,
    AppBreadcrumbModule,
    AppHeaderModule,
    AppFooterModule,
    AppSidebarModule,
} from '@coreui/angular';

// Import routing module
import {AppRoutingModule} from './app.routing';

// Import 3rd party components
import {AuthEffects, AuthModule, StorageService} from "@cosbeni/auth";
import {StoreModule} from "@ngrx/store";
import {EffectsModule} from "@ngrx/effects";
import {AngularFileUploaderModule} from "angular-file-uploader";
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {ModalModule} from "ngx-bootstrap/modal";
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {TabsModule} from 'ngx-bootstrap/tabs';
import {NgxYubinBangoModule} from 'ngx-yubinbango';
import {DragulaModule} from "ng2-dragula";
import {ChartsModule} from 'ng2-charts';
import {WjCoreModule} from "@grapecity/wijmo.angular2.core";
import {WjInputModule} from "@grapecity/wijmo.angular2.input";
import {WjGridModule} from "@grapecity/wijmo.angular2.grid";
import {WjGridFilterModule} from "@grapecity/wijmo.angular2.grid.filter";
import {WjChartModule} from "@grapecity/wijmo.angular2.chart";
import {WjNavModule} from "@grapecity/wijmo.angular2.nav";
import {WjOlapModule} from "@grapecity/wijmo.angular2.olap";
import {WjGridMultirowModule} from "@grapecity/wijmo.angular2.grid.multirow";
import {ToasterModule} from 'angular2-toaster/angular2-toaster';
import {DayPilotModule} from "daypilot-pro-angular";
import {FormHelperService} from "./services/form-helper.service";
import {AdminGuard} from "./services/admin.guard";
import {AuthGuard} from "./services/auth.guard";

import {LoginComponent} from "./pages/login.component";
import * as wjcCore from "@grapecity/wijmo";
import * as fromKaitoriya from "./ngrx/reducers/index";
import {DatabaseEffects, DatabaseService, DataService} from "@cosbeni/database";
import {ADMIN_COMPONENTS} from "./admin";
import {USER_COMPONENTS} from "./user";
import {LoadingService} from "./services/loading.service";
import {LoadingInterceptor} from "./services/loading.interceptor";
import {ConfirmComponent} from "./ui/modal";
import {SpinnerComponent} from "./ui/spinner/spinner.component";
import {UserEffects} from "./ngrx/effects";
import {TagInputModule} from "./ui/tag-input/tag-input.module";
import {BreadcrumbsComponent} from "./ui/breadcrumb/breadcrumb.component";
import {WhiteboardComponent} from "./ui/whiteboard/whiteboard.component";
import {GapiSession} from "./gapi/gapi.session";
import {KaitoriyaConfig} from './config/kaitoriya.config';

export function initGapi(gapiSession: GapiSession) {
    return () => gapiSession.initClient();
}

// 旧システム時代の localStorage (database=kaitoriya 等) が残っていると
// AuthGuard の token prefix やログイン先が旧 DB 名のままになるため、
// 起動時に現行 DB 名 (KaitoriyaConfig.DATABASE) へ強制的に上書きし、
// 旧 DB 名で保存された url / token を破棄して再ログインさせる
export function initStorageMigration(storage: StorageService) {
    return () => {
        const stored = storage.get("database");
        if (stored && stored !== KaitoriyaConfig.DATABASE) {
            storage.remove(stored + "_url");
            storage.remove(stored + "_token");
            storage.set("database", KaitoriyaConfig.DATABASE, "");
        }
    };
}

//wjcCore.setLicenseKey('275584172674125#B0ijPyNHZisnOiwmbBJye0ICRiwiI34zZ4U6TrQmVWZEczc5UJVjNvE4ZhVXdSJ6U7gHW4MzQwRkSvBzLWtyb5gDWThUUM9mSihXYKxER5U7Zz36Mp5UT7AzKxk5RohmZsNlS6VzYvJUbTlTT7ZHZ6ATS9BzTNpEaTdXWjREa8F4NZtUSEVjevE7NwxkY4YXR8MlU7FEZ5UVaDpne9oFMSlzV7YDbLNmUM3GNLZmUr8kSGdVVyp7Vih4dupGM0h5Z8E4N8pHc9sWaxRzR8VVNOVlWWVjS8VmNTZnZU96KKFWYrcnVxoHbEhXTxUUdvVEdvMnSxE5Vk5kURtUZatiZEhXU9IWcxYjbqFndrBVODJ4ZWNWNP3kR8hFSHN6N9d6YKVTVul4YDVzUORlYrxWby3UTrEFVzEHWKFlQiFFbqtWREdUWJp7Zz24dwIFMXhXYtBlMDNHTk5UaZtyMEpnbFdWOrgmcGdESJBFeWlkI0IyUiwiIzATNGJjNFdjI0ICSiwiNyYDMxkjMyITM0IicfJye&Qf35VfikEMyIlI0IyQiwiIu3Waz9WZ4hXRgACdlVGaThXZsZEIv5mapdlI0IiTisHL3JSNJ9UUiojIDJCLi86bpNnblRHeFBCIyV6dllmV4J7bwVmUg2Wbql6ViojIOJyes4nILdDOIJiOiMkIsIibvl6cuVGd8VEIgc7bSlGdsVXTg2Wbql6ViojIOJyes4nI4YkNEJiOiMkIsIibvl6cuVGd8VEIgAVQM3EIg2Wbql6ViojIOJyes4nIzMEMCJiOiMkIsISZy36Qg2Wbql6ViojIOJyes4nIVhzNBJiOiMkIsIibvl6cuVGd8VEIgQnchh6QsFWaj9WYulmRg2Wbql6ViojIOJyebpjIkJHUiwiIxQjMwUDMgkjM7ATOxAjMiojI4J7QiwiIx8CMuAjL7ITMiojIz5GRiwiI+S09ayL9Pyb9qCq9jK88GO887K88XO882O88sO88wK88iojIh94QiwiI5ITM4cjNycTM4gTN5cjMiojIklkIs4nIyYXOxAjMiojIyVmdiwSZzyWXm');
wjcCore.setLicenseKey('kaitoriya.info,614521977631756#B0HbhZmOiI7ckJye0ICbuFkI1pjIEJCLi4TPBVlYDZjVwJjZoN4YPRHdqNUM0BTNuZ7Zrg5N9ZkT5Y6YThTVu5Ua6NzRwEGRNRndYFzNTd4KKpHZWRWa8RzTjZXdTpXM6knU82Uc5YlWIh5N8dnVxUlTWJTWmFza4cVasR7akZnSQJ6NvhnYhJ7LzJ4TEdGWWZWNCtGSmFkaylVeaZDaWJGaOBDcqtCZVB7SrNjZZhTMmdVS55maMxkUvEmblR5UiJ4Ri5ERxQUVhJnZnB7andXbN3iVv8mevhldzZXeFt4NIlmbrUnRPJVWHl7U5V4MMdVR9UHWyAVOz5WT784NsZkV9kDM7RWeYRESQ96coVDUNVmZCVVWGdDbUNTe4YjWBBnRMNUUzl5dxY6aZJnS6EDbvJ5ZwkVSyRWVxZnYQ3SNv2keRpEdy9mMwIFZjZzShJWSroHTwEXcYpWMEN7bv2WVvRnSpNFc5oWMxhnNPNUYiojITJCLigjMDBDRyQjI0ICSiwSOwAjNzAzMyYTM0IicfJye#4Xfd5nIJBjMSJiOiMkIsIibvl6cuVGd8VEIgQXZlh6U8VGbGBybtpWaXJiOi8kI1xSfiUTSOFlI0IyQiwiIu3Waz9WZ4hXRgAicldXZpZFdy3GclJFIv5mapdlI0IiTisHL3JyS7gDSiojIDJCLi86bpNnblRHeFBCI73mUpRHb55EIv5mapdlI0IiTisHL3JCNGZDRiojIDJCLi86bpNnblRHeFBCIQFETPBCIv5mapdlI0IiTisHL3JyMDBjQiojIDJCLiUmcvNEIv5mapdlI0IiTisHL3JSV8cTQiojIDJCLi86bpNnblRHeFBCI4JXYoNEbhl6YuFmbpZEIv5mapdlI0IiTis7W0ICZyBlIsIyMwgjM8ADI4IjMwIjMwIjI0ICdyNkIsIybm9WauEWepJ7b4lWYrJiOiMXbEJCLis8gjn9gjnrgjPrgj1LpnrJvk/IvlrKomLiOiEmTDJCLiYTN7EzM6czN9EjM5QTM6IiOiQWSiwSfdtlOicGbmJCLiEjdyIDMyIiOiIXZ6JLLcN')
Stimulsoft.Base.StiLicense.Key = '6vJhGtLLLz2GNviWmUTrhSqnOItdDwjBylQzQcAOiHl26ZSMEMFmk3V4cVIW/z4aEPCZyhHy36IUIO31u64kPjupS5' +
    'RJ4Kkjh5zA8Js69gML1a/uLIe1uPsavO7G5SbuLvcEW0DcE58buMSZAh4scMesvQVrMM5AkiDUBomhUQfwCMrOWHM9' +
    '61Nb0khu5OVxh450w7X/qNMLW/Ncr+Djtsa1cbZzEQ4r7VkoHy7uZEPVVX9G3Qqtt/dB0cY62D4SY5Zal1nD/bV66S' +
    '3ok4BE+uUYDR5LQ+Yr1dtr+aM/BJUdeMdY6UGOaMthyAGzcyJ1CfV+9AQNrprnDoMQLK83sxIpn5aTSX/p/LNdTCZa' +
    'gpBGH4fp2hjcWVrAsVgkNVsL9zaA0v83O3CtoSlmJLK7M7IAmtsV3fyy3LRo6PlMmBmsPJMbsfQdIj9krxN3Hx9IEQ' +
    'QpIyo+/vGqtsCpDAFfQAmcv6UfTYw0j3XWdoiRQWNBhOjx1jRZwogSJ0wzdVnqa78HySzEfTFNpyWyiPFl7WBxTNnB' +
    'wAVazg9gu7dVCbvUJJA+BRdINej8KErdRgfy';

@NgModule({
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        AngularFileUploaderModule,
        AppRoutingModule,
        AppAsideModule,
        AppBreadcrumbModule.forRoot(),
        AppFooterModule,
        AppHeaderModule,
        AppSidebarModule,
        AuthModule.forRoot({}),
        DayPilotModule,
        FormsModule,
        ReactiveFormsModule,
        PerfectScrollbarModule,
        BsDropdownModule.forRoot(),
        DragulaModule.forRoot(),
        ModalModule.forRoot(),
        NgxYubinBangoModule,
        TabsModule.forRoot(),
        TooltipModule.forRoot(),
        TabsModule.forRoot(),
        ChartsModule,
        WjCoreModule,
        WjInputModule,
        WjGridModule,
        WjGridFilterModule,
        WjChartModule,
        WjNavModule,
        WjOlapModule,
        WjGridMultirowModule,
        TagInputModule,
        ToasterModule.forRoot(),
        StoreModule.forRoot({}, {
                runtimeChecks: {
                    strictStateImmutability: false,
                    strictActionImmutability: false,
                }
            }
        ),
        StoreModule.forFeature('kaitoriyaFeature', fromKaitoriya.reducer),
        EffectsModule.forRoot([AuthEffects, DatabaseEffects, UserEffects]),
    ],
    declarations: [
        AppComponent,
        LoginComponent,
        ...APP_CONTAINERS,
        ADMIN_COMPONENTS,
        USER_COMPONENTS,
        BreadcrumbsComponent,
        ConfirmComponent,
        SpinnerComponent,
        WhiteboardComponent
    ],
    providers: [{
        provide: LocationStrategy,
        useClass: HashLocationStrategy
    },
        {provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true},
        {provide: APP_INITIALIZER, useFactory: initGapi, deps: [GapiSession], multi: true},
        {provide: APP_INITIALIZER, useFactory: initStorageMigration, deps: [StorageService], multi: true},
        FormHelperService,
        AdminGuard,
        AuthGuard,
        DataService,
        DatabaseService,
        GapiSession,
        LoadingService
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}
